import { useMemo } from "react";
import { DataTable, IconButton } from "../../../components";
import DateColumn from "./DateColumn";
import { FaUserPlus } from "react-icons/fa";
import { useAuthenticationService } from "../../../services/authentication.service";
import { roles } from "../../../utils/constants";

// Base column definitions (used internally)
const baseColumns = [
  {
    key: "actions",
    dataKey: "id",
    title: "New Match",
    width: 90,
    sortable: false,
    filterable: false,
    render: (rowData, onMatchChild) => (
      <IconButton
        icon={<FaUserPlus />}
        size="sm"
        appearance="primary"
        onClick={() => onMatchChild && onMatchChild(rowData)}
        title="Match to Child"
        className="create-new-match"
        height={30}
        width={30}
      />
    ),
  },
  {
    key: "date",
    dataKey: "date",
    title: "Date",
    width: 100,
    sortable: true,
    filterable: false,
    fixed: true,
    type: "date",
    render: (rowData) => <DateColumn dataKey="date" rowData={rowData} />,
  },
  {
    key: "centreName",
    dataKey: "centreName",
    title: "Centre",
    filterable: true,
    width: 200,
    sortable: true,
  },
  {
    key: "amount",
    dataKey: "amount",
    title: "Amount",
    width: 120,
    sortable: true,
    filterable: true,
    type: 'number',
    render: (rowData) => {
      const amt = Number(rowData.amount);
      if (!Number.isFinite(amt)) return rowData.amount ?? "";
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      }).format(amt);
    },
  },
  {
    key: "childId",
    dataKey: "childId",
    title: "Child ID",
    width: 120,
    sortable: true,
    filterable: true,
    type: 'string',
  },
  {
    key: "otherParty",
    dataKey: "otherParty",
    title: "Other Party",
    width: 228,
    flexGrow: 2,
    sortable: true,
    filterable: true,
    type: 'string',
  },
  {
    key: "otherPartyAccount",
    dataKey: "otherPartyAccount",
    title: "Other Party Account",
    width: 200,
    sortable: true,
    filterable: true,
    type: 'string',
  },

 
];

const supportColumns = [
  {
    key: "particulars",
    dataKey: "particulars",
    title: "Particulars",
    flexGrow: 1,
    sortable: true,
    filterable: true,
    type: 'string',
  },
  {
    key: "code",
    dataKey: "code",
    title: "Code",
    width: 152,
    flexGrow: 1,
    sortable: true,
    filterable: true,
    type: 'string',
  },
  {
    key: "reference",
    dataKey: "reference",
    title: "Reference",
    flexGrow: 1,
    width: 152,
    sortable: true,
    filterable: true,
    type: 'string',
  },
   {
    key: "type",
    dataKey: "type",
    title: "Type",
    width: 90,
    sortable: true,
    filterable: true,
    type: 'string',
  },
  {
    key: "ignoreReason",
    dataKey: "ignoreReason",
    title: "Ignore Reason",
    width: 200,
    sortable: true,
    filterable: true,
    type: 'string',
  },
];
const financeColumns = [

  {
    key: "fileTypeName",
    dataKey: "fileTypeName",
    title: "File Type",
    width: 150,
    sortable: true,
    filterable: true,
    type: 'string',
  },
  {
    key: "expectedBankAccount",
    dataKey: "expectedBankAccount",
    title: "Dest. Bank Account",
    flexGrow: 1,
    width: 200,
    sortable: true,
    filterable: true,
    type: 'string',
  }

];

/**
 * Column metadata for use with hooks (without render functions)
 * This is a lightweight version used by useTableData for filtering/sorting logic
 */
// Include base, support and finance columns in the lightweight metadata
// used by useTableData (filters/sorting). Previously only baseColumns were
// exported which left finance/support filter lists empty.
export const transactionColumnMetadata =
  baseColumns.concat(supportColumns, financeColumns).map(({ key, dataKey, type, filterable, sortable }) => ({
    key,
    dataKey,
    type,
    filterable,
    sortable,
  }));


export function buildTransactionColumns({ isAdmin, isFinance, isSupport, onMatchChild }) {
  let cols = [...baseColumns];
  
  // Remove actions column for non-admins
  if (!isAdmin) {
    cols = cols.filter(col => col.key !== 'actions');
  }
  
  // Add role-specific columns
  if (isFinance) {
    cols = cols.concat(financeColumns);
  }
  if (isSupport) {
    cols = cols.concat(supportColumns);
  }

  // Map columns with proper render functions
  return cols.map(col => ({
    ...col,
    render: col.render ? (rowData, rowIndex) => {
      // Pass onMatchChild to actions column render
      if (col.key === 'actions') {
        return col.render(rowData, onMatchChild);
      }
      return col.render(rowData, rowIndex);
    } : undefined
  }));
}

function TransactionTable({
  data,
  allData, // All data across pages for total calculation
  sortColumn,
  sortType,
  onSortColumn,
  loading,
  activeFilters,
  onFilterClick,
  // Pagination props
  currentPage,
  pageSize,
  totalItems,
  onPageChange,
  onPageSizeChange,
  // Match functionality
  onMatchChild,
}) {
  const authService = useAuthenticationService();
  const isAdmin = useMemo(() => authService.isInAnyRole(roles.ADMINS), [authService]);
  const isFinance = useMemo(() => authService.isInAnyRole(roles.FINANCE), [authService]);
  const isSupport = useMemo(() => authService.isInAnyRole(roles.SUPPORT), [authService]);

  // Build columns with role-based filtering and proper render functions
  const columns = useMemo(
    () => buildTransactionColumns({ isAdmin, isFinance, isSupport, onMatchChild }),
    [isAdmin, isFinance, isSupport, onMatchChild]
  );

  // Calculate sum of amounts from ALL filtered data (not just current page)
  const totalAmount = useMemo(() => {
    const dataToSum = allData || data || [];
    return dataToSum.reduce((sum, row) => {
      const amt = Number(row.amount);
      return Number.isFinite(amt) ? sum + amt : sum;
    }, 0);
  }, [allData, data]);

  // Create footer content for the amount sum
  const footer = useMemo(() => (
    <div style={{ fontWeight: 'bold' }}>
      Total Amount: {new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      }).format(totalAmount)} ({(allData || data)?.length || 0} rows)
    </div>
  ), [totalAmount, allData, data]);

  return (
    <DataTable
      className="transaction-table"
      shouldUpdateScroll={true}
      fillHeight
      data={data}
      columns={columns}
      sortColumn={sortColumn}
      sortType={sortType}
      onSortColumn={onSortColumn}
      loading={loading}
      activeFilters={activeFilters}
      onFilterClick={onFilterClick}
      expandable={false}
      showRowCount={true}
      currentPage={currentPage}
      pageSize={pageSize}
      totalItems={totalItems}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      limitOptions={[15, 30, 45]}
      rowKey="id"
      footer={footer}
    />
  );
}

export default TransactionTable;
